import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { Answer, Button } from 'storybook-ui'

import { Container } from 'components/Container'
import { FooterContainer } from 'components/FooterContainer'
import { OptionType } from 'components/Option'
import { TermsOfUseLink } from 'components/TermsOfUseLink'

import {
  extendSubscriptionWithFreePeriodAction,
  unsubscribeUserAction,
} from 'root-redux/actions/user'
import { selectAppName } from 'root-redux/selects/common'
import { selectUUID } from 'root-redux/selects/user'
import { TAppDispatch } from 'root-redux/store/store'

import { RetentionStep } from 'modules/unsubscribe/components/RetentionPageContainer/constants'
import { StyledDiscountOffer as S } from 'modules/unsubscribe/pages/DiscountOffer/DiscountOffer.styles'

import { eventLogger } from 'services/eventLogger.service'

import { goTo } from 'browser-history'
import { PageId } from 'page-constants'
import {
  APP_BUTTON_THEMES,
  APP_OPTION_THEMES,
  EVENT_SOURCE,
} from 'root-constants'

import {
  FREE_MONTH_OFFER_BENEFITS,
  FREE_MONTH_OFFER_OPTION_VALUES,
  FREE_MONTH_OFFER_SCREEN_QUESTION,
  FREE_PERIOD_IN_MONTHS,
} from './constants'

export const FreeMonthOffer: React.FC = () => {
  const { search } = useLocation()
  const { t } = useTranslation()
  const dispatch: TAppDispatch = useDispatch()
  const appName = useSelector(selectAppName)
  const userId = useSelector(selectUUID)

  const [answer, setAnswer] = useState('')

  const freePeriod = t('subscription.billingPeriods.month', {
    count: FREE_PERIOD_IN_MONTHS,
  })

  useEffect(() => {
    eventLogger.logDiscountOfferScreenShow(
      FREE_MONTH_OFFER_SCREEN_QUESTION,
      userId,
    )
  }, [userId])

  const optionProps = {
    name: PageId.DISCOUNT_OFFER,
    type: OptionType.RADIO,
    theme: APP_OPTION_THEMES[appName],
    onChange: (value: string) => {
      setAnswer(value)
    },
  }

  const acceptFreeMonthOffer = () => {
    dispatch(extendSubscriptionWithFreePeriodAction(FREE_PERIOD_IN_MONTHS))
    goTo({
      pathname: PageId.CONTINUED_SUBSCRIPTION,
      search,
    })
  }

  const cancelSubscription = () => {
    dispatch(unsubscribeUserAction())
    goTo({
      pathname: PageId.CANCELED_SUBSCRIPTION,
      search,
    })
  }

  const handleAnswer = () => {
    eventLogger.logDiscountOfferScreenClose(answer, userId)

    if (answer === FREE_MONTH_OFFER_OPTION_VALUES.NO) {
      cancelSubscription()
      return
    }

    acceptFreeMonthOffer()
  }

  return (
    <S.Wrapper step={RetentionStep.THIRD}>
      <Container>
        <S.Description>
          <Trans
            i18nKey="retention.freeMonthOffer.description"
            values={{ period: freePeriod }}
          />
        </S.Description>
        <S.Benefits>
          {FREE_MONTH_OFFER_BENEFITS.map((benefitKey) => (
            <S.Benefit key={benefitKey}>{t(benefitKey)}</S.Benefit>
          ))}
        </S.Benefits>
        <S.Question>
          {t('retention.discountOffer.title', { context: appName })}
        </S.Question>
        <S.OptionsContainer>
          <Answer {...optionProps} value={FREE_MONTH_OFFER_OPTION_VALUES.YES}>
            <Trans
              i18nKey="retention.freeMonthOffer.yesOffer"
              values={{ period: freePeriod }}
            />
          </Answer>
          <Answer {...optionProps} value={FREE_MONTH_OFFER_OPTION_VALUES.NO}>
            {t`retention.discountOffer.noOffer`}
          </Answer>
        </S.OptionsContainer>
        <S.Disclaimer>
          {t`retention.freeMonthOffer.disclaimer`}{' '}
          <TermsOfUseLink
            source={EVENT_SOURCE.DISCOUNT_OFFER}
            text={t`retention.discountOffer.subscriptionTerms`}
          />
        </S.Disclaimer>
      </Container>
      <FooterContainer>
        <Button
          onClick={handleAnswer}
          disabled={!answer}
          theme={APP_BUTTON_THEMES[appName]}
        >
          {t`button.continue`}
        </Button>
      </FooterContainer>
    </S.Wrapper>
  )
}
